"use client";

import { Button } from "@/components/new/button";
import { BASE_URL } from "@/lib/constants";
import { formatImageUrl, parsePostContentJSON } from "@/lib/utils";
import PinnedPostsCarousel from "@/modules/community/components/feed/pinned-posts-carousel";
import JoinCommunityButton from "@/modules/community/components/join-community-button";
import ProfileTooltip from "@/modules/profile/components/tooltip";
import { useAuth } from "@/providers/auth-provider";
import { CommunityProvider } from "@/providers/community-provider";
import { useModal } from "@/providers/modal-provider";
import { ModalType } from "@/types/modal-type";
import { TGetCategoryDataResponseDTO } from "@/types/dto/category.dto";
import { ActivitySquare, ArrowUpRightIcon, CheckIcon, Download, EyeIcon } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import ListingAmbassadors from "./ambassadors";
import { ModeratorProvider } from "@/providers/moderator-provider";

type CategoryHeroSectionProps = {
	name: string;
	description: string;
	logo: string;
	ambassadors: TGetCategoryDataResponseDTO["ambassadors"];
	leaders?: TGetCategoryDataResponseDTO["leaders"];
	features?: TGetCategoryDataResponseDTO["features"];
	viewsCount: TGetCategoryDataResponseDTO["viewsCount"];
	categorySlug: string;
	channelMembers?: { image: string; username: string }[];
	posts?: TGetCategoryDataResponseDTO["posts"];
	communityId?: string;
	channelId?: string;
	channelMembersCount: number;
	buyingGuideLink?: string;
};

const CategoryHeroSection = ({
	categorySlug,
	name,
	description,
	logo,
	ambassadors,
	leaders,
	features,
	viewsCount,
	posts,
	communityId,
	channelId,
	channelMembers,
	channelMembersCount,
	buyingGuideLink,
}: CategoryHeroSectionProps) => {
	const { isLoggedIn } = useAuth();
	const { openModal } = useModal();

	const visibleChannelMembers = channelMembers?.slice(0, 3);
	const visibleLeaders = leaders?.slice(0, 5);

	const handleDownloadClick = async (event: React.MouseEvent) => {
		event.preventDefault();

		if (!isLoggedIn) {
			openModal(ModalType.Auth);
			return;
		}

		try {
			await fetch(`${BASE_URL}/api/notify-zoho`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					categoryName: name,
				}),
			});
		} catch (error) {
			console.error("Failed to notify Zoho Cliq:", error);
		}

		window.open(buyingGuideLink, "_blank", "noopener,noreferrer");
	};

	return (
		<div className="mx-auto mb-14 max-w-6xl px-4 pt-6 lg:px-0">
			<div className="mb-6 flex flex-col gap-8 lg:flex-row lg:items-start lg:justify-between lg:gap-16">
				<div className="flex flex-col gap-5 lg:basis-2/3">
					<div className="flex flex-col items-start gap-6 lg:flex-row lg:items-center">
						{logo ? (
							<div className="flex items-center justify-center rounded-2xl">
								<Image
									src={logo}
									alt={name}
									width={80}
									height={80}
									quality={100}
									className="h-[5rem] min-h-[5rem] w-[5rem] min-w-[5rem] object-contain"
								/>
							</div>
						) : (
							<div className="flex items-center justify-center rounded-2xl bg-gradient-to-br from-brand-primary/5 via-brand-primary to-brand-primary p-4">
								<ActivitySquare className="h-12 w-12 text-white" />
							</div>
						)}

						<h1 className="text-3xl font-bold leading-snug tracking-tighter lg:text-5xl">{name}</h1>
					</div>

					<p className="my-2 text-xl text-gray-600">{description}</p>

					<div className="my-12 flex flex-col gap-8 lg:my-4 lg:flex-row lg:justify-between lg:gap-16">
						<div className="flex flex-col gap-8">
							{features && features?.length > 0 && (
								<div className="grid w-full grid-cols-1 gap-4 md:grid-cols-2 lg:gap-6">
									{features?.map((feature) => (
										<div key={feature.label} className="flex items-center gap-2">
											<div className="flex h-8 w-8 items-center justify-center rounded bg-white">
												<CheckIcon className="h-6 w-6 text-brand-primary" />
											</div>
											<span className="font-semibold">{feature.label}</span>
										</div>
									))}
								</div>
							)}

							<div className="flex items-center gap-2">
								<EyeIcon className="h-8 w-8 text-gray-500" />
								<span className="text-lg font-medium text-gray-500">{viewsCount} Views</span>
							</div>
						</div>
					</div>
				</div>

				<div className="flex flex-col items-center justify-center gap-7 lg:basis-1/3">
					<ListingAmbassadors ambassadors={ambassadors} />

					{channelMembers && channelMembers.length > 0 && (
						<div className="flex items-center gap-2">
							<div className="flex -space-x-3">
								{visibleChannelMembers?.map((member) => (
									<Link
										key={member.username}
										href={`/user/${member.username}`}
										className="relative"
									>
										<div className="h-[47px] w-[47px] rounded-full bg-white p-[2px]">
											<Image
												src={formatImageUrl(member.image) || "/images/person-2.jpg"}
												alt={member.username}
												width={50}
												height={50}
												className="h-[46px] w-[46px] rounded-full object-cover"
											/>
										</div>
									</Link>
								))}
							</div>

							<span className="text-[15px] font-semibold text-[#1A2133]">
								{channelMembersCount}+ Followers
							</span>
						</div>
					)}

					<CommunityProvider>
						{communityId && channelId ? (
							<JoinCommunityButton
								communityId={communityId}
								channelId={channelId}
								className="h-[55px] w-[355px]"
								actionLabel="Follow"
								completedLabel="Followed"
							/>
						) : null}
					</CommunityProvider>

					{buyingGuideLink && (
						<Button
							onClick={handleDownloadClick}
							className="flex h-[55px] w-[355px] items-center justify-center gap-2 rounded-[6px] bg-[#10172A] px-4 py-3 text-center text-base text-white"
						>
							<Download className="h-[18px] w-[18px] text-white" />
							{categorySlug === "fractional-investment"
								? "The Ultimate Guide to Fractional Investing"
								: categorySlug === "real-estate-crm-portal"
								? "The Ultimate CRM Buying Guide"
								: "The Ultimate Buying Guide"}
						</Button>
					)}

					{visibleLeaders && visibleLeaders.length > 0 && (
						<div>
							<div className="mb-4 flex w-full items-center gap-1">
								<span className="text-lg font-medium uppercase tracking-widest text-gray-500 md:text-xl">
									Leaders
								</span>
								<ArrowUpRightIcon className="h-6 w-6 text-gray-500" />
							</div>
							<div className="flex justify-center space-x-6">
								{visibleLeaders?.map((leader) => (
									<Link
										key={leader.UserProfile.username}
										href={`/user/${leader.UserProfile.username}`}
									>
										<div
											key={leader.UserProfile.username}
											className="flex flex-col gap-1 text-center"
										>
											<ProfileTooltip
												title={leader.name}
												image={leader.image || ""}
												username={leader.UserProfile.username}
												isFounder={leader.UserProfile.isFounder}
												isVerified={leader.UserProfile.isVerified}
												description={`${
													leader.UserProfile.jobTitle ||
													leader.UserProfile.howDoYouIdentifyYourself ||
													"-"
												} | ${leader.UserProfile.company || "-"}`}
											/>
										</div>
									</Link>
								))}
							</div>
						</div>
					)}
				</div>
			</div>

			{posts && posts.length > 0 && (
				<CommunityProvider>
					<ModeratorProvider>
						{/* TODO (zeekhuge): The parsePostContentJSON is being used with `!` at a lot of places. But why is it even return null ? This needs to be refactored */}
						<PinnedPostsCarousel
							posts={posts.map((e) => parsePostContentJSON(e)!)}
							showPinnedText={false}
							uiType="MINIMAL"
						/>
					</ModeratorProvider>
				</CommunityProvider>
			)}
		</div>
	);
};

export default CategoryHeroSection;
